/**
 * Catálogo CONTRAN de placas de sinalização vertical.
 * Baseado no Manual Brasileiro de Sinalização de Trânsito (CONTRAN).
 *
 * Cada placa tem:
 *   - codigo:    ex.: "R-1"
 *   - nome:      descrição oficial curta
 *   - forma:     "circulo" | "octogono" | "triangulo" | "losango" | "retangulo"
 *   - categoria: chave do grupo
 *   - tamanhos:  objeto com tamanhos padronizados (varia por classe da via)
 *
 * tamanhos padrão retornam:
 *   - circulo   → { diametro_cm }
 *   - octogono  → { lado_cm }
 *   - triangulo → { lado_cm }   (triângulo equilátero)
 *   - losango   → { lado_cm }   (quadrado 45°, área = lado²)
 *   - retangulo → { largura_cm, altura_cm }
 */

// Tamanhos padrão Tecnovias por classe da via (regulamentação circular)
export const TAMANHOS_CIRCULAR = {
  "Especial": { diametro_cm: 120 },
  "Grande":   { diametro_cm: 100 },
  "Médio":    { diametro_cm: 80 },
  "Pequeno":  { diametro_cm: 60 },
};

// Advertência (losango) — padrão Tecnovias
export const TAMANHOS_LOSANGO = {
  "Especial": { lado_cm: 120 },
  "Grande":   { lado_cm: 100 },
  "Médio":    { lado_cm: 80 },
  "Pequeno":  { lado_cm: 60 },
};

// Placas retangulares (orientativa, educativa, turística, indicativa) — padrão Tecnovias
export const TAMANHOS_INDICACAO = {
  "Padrão A":  { largura_cm: 200, altura_cm: 100 },
  "Padrão B":  { largura_cm: 200, altura_cm: 120 },
  "Padrão C":  { largura_cm: 300, altura_cm: 150 },
  "Padrão D":  { largura_cm: 300, altura_cm: 120 },
};

function tamanhosParaForma(forma, codigo) {
  if (forma === "circulo")   return TAMANHOS_CIRCULAR;
  if (forma === "losango")   return TAMANHOS_LOSANGO;
  if (forma === "retangulo") return TAMANHOS_INDICACAO;
  return {};
}

// Dispositivos de segurança (balizadores, delineadores etc.) — padrão Tecnovias
export const TAMANHOS_DISPOSITIVO = {
  "Padrão A":  { largura_cm: 30, altura_cm: 90 },
  "Padrão B":  { largura_cm: 50, altura_cm: 60 },
};

// ------------------- Regulamentação (R) -------------------
const REGULAMENTACAO = [
  // Padrão Tecnovias: R-1 e R-2 são medidas como placas redondas (Ø → π·d²/4), mesmos tamanhos das circulares
  { codigo: "R-1",  nome: "Parada obrigatória",              forma: "circulo", nota: "Octógono medido como placa redonda (padrão Tecnovias)" },
  { codigo: "R-2",  nome: "Dê a preferência",                forma: "circulo", nota: "Triângulo medido como placa redonda (padrão Tecnovias)" },
  { codigo: "R-3",  nome: "Sentido proibido",                forma: "circulo" },
  { codigo: "R-4a", nome: "Proibido virar à esquerda",       forma: "circulo" },
  { codigo: "R-4b", nome: "Proibido virar à direita",        forma: "circulo" },
  { codigo: "R-5a", nome: "Proibido retornar à esquerda",    forma: "circulo" },
  { codigo: "R-5b", nome: "Proibido retornar à direita",     forma: "circulo" },
  { codigo: "R-6a", nome: "Proibido estacionar",             forma: "circulo" },
  { codigo: "R-6b", nome: "Estacionamento regulamentado",    forma: "circulo" },
  { codigo: "R-6c", nome: "Proibido parar e estacionar",     forma: "circulo" },
  { codigo: "R-7",  nome: "Proibido ultrapassar",            forma: "circulo" },
  { codigo: "R-8a", nome: "Proibido mudar de faixa esq→dir", forma: "circulo" },
  { codigo: "R-8b", nome: "Proibido mudar de faixa dir→esq", forma: "circulo" },
  { codigo: "R-9",  nome: "Proibida a circulação de caminhões", forma: "circulo" },
  { codigo: "R-10", nome: "Proibida a circulação de veículos automotores", forma: "circulo" },
  { codigo: "R-11", nome: "Proibida a circulação de trator",  forma: "circulo" },
  { codigo: "R-12", nome: "Proibida a circulação de bicicletas", forma: "circulo" },
  { codigo: "R-13", nome: "Proibida a circulação de tração animal", forma: "circulo" },
  { codigo: "R-14", nome: "Peso bruto total máximo permitido", forma: "circulo" },
  { codigo: "R-15", nome: "Altura máxima permitida",          forma: "circulo" },
  { codigo: "R-16", nome: "Largura máxima permitida",         forma: "circulo" },
  { codigo: "R-17", nome: "Peso máximo permitido por eixo",   forma: "circulo" },
  { codigo: "R-18", nome: "Comprimento máximo permitido",     forma: "circulo" },
  { codigo: "R-19", nome: "Velocidade máxima permitida",      forma: "circulo" },
  { codigo: "R-20", nome: "Proibido acionar buzina",          forma: "circulo" },
  { codigo: "R-21", nome: "Alfândega",                        forma: "circulo" },
  { codigo: "R-22", nome: "Uso obrigatório de corrente",      forma: "circulo" },
  { codigo: "R-23", nome: "Conserve-se à direita",            forma: "circulo" },
  { codigo: "R-24a",nome: "Sentido de circulação da via",     forma: "retangulo" },
  { codigo: "R-24b",nome: "Passagem obrigatória",             forma: "circulo" },
  { codigo: "R-25a",nome: "Vire à esquerda",                  forma: "circulo" },
  { codigo: "R-25b",nome: "Vire à direita",                   forma: "circulo" },
  { codigo: "R-25c",nome: "Siga em frente ou à esquerda",     forma: "circulo" },
  { codigo: "R-25d",nome: "Siga em frente ou à direita",      forma: "circulo" },
  { codigo: "R-26", nome: "Siga em frente",                   forma: "circulo" },
  { codigo: "R-27", nome: "Ônibus, caminhões e veíc. grande porte, mantenham-se à direita", forma: "circulo" },
  { codigo: "R-28", nome: "Duplo sentido de circulação",      forma: "circulo" },
  { codigo: "R-29", nome: "Proibido trânsito de pedestres",   forma: "circulo" },
  { codigo: "R-30", nome: "Pedestre, ande pela esquerda",     forma: "circulo" },
  { codigo: "R-31", nome: "Pedestre, ande pela direita",      forma: "circulo" },
  { codigo: "R-32", nome: "Circulação exclusiva de ônibus",   forma: "circulo" },
  { codigo: "R-33", nome: "Sentido de circulação na rotatória", forma: "circulo" },
  { codigo: "R-34", nome: "Circulação exclusiva de bicicletas", forma: "circulo" },
  { codigo: "R-35a",nome: "Ciclista, transite à esquerda",    forma: "circulo" },
  { codigo: "R-35b",nome: "Ciclista, transite à direita",     forma: "circulo" },
  { codigo: "R-36a",nome: "Ciclistas à esquerda, pedestres à direita", forma: "circulo" },
  { codigo: "R-36b",nome: "Pedestres à esquerda, ciclistas à direita", forma: "circulo" },
  { codigo: "R-37", nome: "Proibido trânsito de motocicletas", forma: "circulo" },
  { codigo: "R-38", nome: "Proibido trânsito de ônibus",      forma: "circulo" },
  { codigo: "R-39", nome: "Circulação exclusiva de caminhões", forma: "circulo" },
  { codigo: "R-40", nome: "Trânsito proibido a carros de mão", forma: "circulo" },
];

// ------------------- Advertência (A) — losango amarelo -------------------
const ADVERTENCIA = [
  { codigo: "A-1a", nome: "Curva acentuada à esquerda",       forma: "losango" },
  { codigo: "A-1b", nome: "Curva acentuada à direita",        forma: "losango" },
  { codigo: "A-2a", nome: "Curva à esquerda",                 forma: "losango" },
  { codigo: "A-2b", nome: "Curva à direita",                  forma: "losango" },
  { codigo: "A-3a", nome: "Pista sinuosa à esquerda",         forma: "losango" },
  { codigo: "A-3b", nome: "Pista sinuosa à direita",          forma: "losango" },
  { codigo: "A-4a", nome: "Curva acentuada em S à esquerda",  forma: "losango" },
  { codigo: "A-4b", nome: "Curva acentuada em S à direita",   forma: "losango" },
  { codigo: "A-5a", nome: "Curva em S à esquerda",            forma: "losango" },
  { codigo: "A-5b", nome: "Curva em S à direita",             forma: "losango" },
  { codigo: "A-6",  nome: "Cruzamento de vias",               forma: "losango" },
  { codigo: "A-7a", nome: "Via lateral à esquerda",           forma: "losango" },
  { codigo: "A-7b", nome: "Via lateral à direita",            forma: "losango" },
  { codigo: "A-8",  nome: "Interseção em T",                  forma: "losango" },
  { codigo: "A-9",  nome: "Bifurcação em Y",                  forma: "losango" },
  { codigo: "A-10a",nome: "Entroncamento oblíquo à esquerda", forma: "losango" },
  { codigo: "A-10b",nome: "Entroncamento oblíquo à direita",  forma: "losango" },
  { codigo: "A-11a",nome: "Junções sucessivas contrárias-esq", forma: "losango" },
  { codigo: "A-11b",nome: "Junções sucessivas contrárias-dir", forma: "losango" },
  { codigo: "A-12", nome: "Interseção em círculo",            forma: "losango" },
  { codigo: "A-13a",nome: "Confluência à esquerda",           forma: "losango" },
  { codigo: "A-13b",nome: "Confluência à direita",            forma: "losango" },
  { codigo: "A-14", nome: "Semáforo à frente",                forma: "losango" },
  { codigo: "A-15", nome: "Parada obrigatória à frente",      forma: "losango" },
  { codigo: "A-16", nome: "Bonde",                            forma: "losango" },
  { codigo: "A-17", nome: "Pista irregular",                  forma: "losango" },
  { codigo: "A-18", nome: "Saliência ou lombada",             forma: "losango" },
  { codigo: "A-19", nome: "Depressão",                        forma: "losango" },
  { codigo: "A-20a",nome: "Declive acentuado",                forma: "losango" },
  { codigo: "A-20b",nome: "Aclive acentuado",                 forma: "losango" },
  { codigo: "A-21a",nome: "Estreitamento de pista ao centro", forma: "losango" },
  { codigo: "A-21b",nome: "Estreitamento à esquerda",         forma: "losango" },
  { codigo: "A-21c",nome: "Estreitamento à direita",          forma: "losango" },
  { codigo: "A-21d",nome: "Alargamento à esquerda",           forma: "losango" },
  { codigo: "A-21e",nome: "Alargamento à direita",            forma: "losango" },
  { codigo: "A-22", nome: "Ponte estreita",                   forma: "losango" },
  { codigo: "A-23", nome: "Ponte móvel",                      forma: "losango" },
  { codigo: "A-24", nome: "Obras",                            forma: "losango" },
  { codigo: "A-25", nome: "Mão dupla adiante",                forma: "losango" },
  { codigo: "A-26a",nome: "Sentido único",                    forma: "losango" },
  { codigo: "A-26b",nome: "Sentido duplo",                    forma: "losango" },
  { codigo: "A-27", nome: "Área com desmoronamento",          forma: "losango" },
  { codigo: "A-28", nome: "Pista escorregadia",               forma: "losango" },
  { codigo: "A-29", nome: "Projeção de cascalho",             forma: "losango" },
  { codigo: "A-30a",nome: "Trânsito de ciclistas",            forma: "losango" },
  { codigo: "A-30b",nome: "Passagem sinalizada de ciclistas", forma: "losango" },
  { codigo: "A-31", nome: "Trânsito de tratores",             forma: "losango" },
  { codigo: "A-32a",nome: "Trânsito de pedestres",            forma: "losango" },
  { codigo: "A-32b",nome: "Passagem sinalizada de pedestres", forma: "losango" },
  { codigo: "A-33a",nome: "Área escolar",                     forma: "losango" },
  { codigo: "A-33b",nome: "Passagem sinalizada de escolares", forma: "losango" },
  { codigo: "A-34", nome: "Crianças",                         forma: "losango" },
  { codigo: "A-35", nome: "Animais",                          forma: "losango" },
  { codigo: "A-36", nome: "Animais selvagens",                forma: "losango" },
  { codigo: "A-37", nome: "Altura limitada",                  forma: "losango" },
  { codigo: "A-38", nome: "Largura limitada",                 forma: "losango" },
  { codigo: "A-39", nome: "Passagem de nível sem barreira",   forma: "losango" },
  { codigo: "A-40", nome: "Passagem de nível com barreira",   forma: "losango" },
  { codigo: "A-41", nome: "Cruz de Santo André",              forma: "retangulo" },
  { codigo: "A-42a",nome: "Início de pista dupla",            forma: "losango" },
  { codigo: "A-42b",nome: "Fim de pista dupla",               forma: "losango" },
  { codigo: "A-42c",nome: "Pista dividida",                   forma: "losango" },
  { codigo: "A-43", nome: "Aeroporto",                        forma: "losango" },
  { codigo: "A-44", nome: "Vento lateral",                    forma: "losango" },
  { codigo: "A-45", nome: "Rua sem saída",                    forma: "losango" },
  { codigo: "A-46", nome: "Peso limitado",                    forma: "losango" },
];

// ------------------- Indicação (I) — retangular verde/azul -------------------
// Placas retangulares por cor (padrão Tecnovias) — sem escolha de código: vai direto para tamanho e quantidade
const retangular = (codigo, nome, tamanhos) => [{ codigo, nome, forma: "retangulo", sem_codigo: true, ...(tamanhos ? { tamanhos } : {}) }];

export const CATEGORIAS_CONTRAN = [
  { key: "Regulamentação", label: "Regulamentação (R)", cor: "#DC2626", cor_fundo: "#FEE2E2", placas: REGULAMENTACAO },
  { key: "Advertência",    label: "Advertência (A)",    cor: "#B45309", cor_fundo: "#FEF3C7", placas: ADVERTENCIA },
  { key: "Orientativa",    label: "Orientativa (verde)", descricao: "Indicativa / orientação de destinos", cor: "#065F46", cor_fundo: "#D1FAE5", sem_codigo: true, placas: retangular("ORI", "Placa orientativa (verde)") },
  { key: "Educativa",      label: "Educativa (branca)",  descricao: "Mensagens educativas",                 cor: "#52525B", cor_fundo: "#F4F4F5", swatch: "#FFFFFF", sem_codigo: true, placas: retangular("EDU", "Placa educativa (branca)") },
  { key: "Turística",      label: "Turística (marrom)",  descricao: "Atrativos e serviços turísticos",      cor: "#78350F", cor_fundo: "#F5D0A9", sem_codigo: true, placas: retangular("TUR", "Placa turística (marrom)") },
  { key: "Indicativa",     label: "Indicativa (azul)",   descricao: "Serviços auxiliares / indicação",      cor: "#1E40AF", cor_fundo: "#DBEAFE", sem_codigo: true, placas: retangular("IND", "Placa indicativa (azul)") },
  { key: "Dispositivo",    label: "Dispositivos de segurança", descricao: "Balizadores, delineadores e afins", cor: "#0A0A0A", cor_fundo: "#FEF3C7", swatch: "#FFCC00", sem_codigo: true, placas: retangular("DSP", "Dispositivo de segurança", TAMANHOS_DISPOSITIVO) },
];

export function getTamanhosPadrao(placa) {
  if (!placa) return {};
  if (placa.tamanhos) return placa.tamanhos;
  return tamanhosParaForma(placa.forma, placa.codigo);
}

export const SUBSTRATOS = [
  { key: "aluminio",  label: "Alumínio" },
  { key: "aco",       label: "Aço" },
  { key: "galvanizada", label: "Chapa galvanizada" },
];

export const PELICULAS = [
  { key: "gt_i",     label: "Grau Técnico I (Comercial)" },
  { key: "gt_ii",    label: "Grau Técnico II (Alta Intensidade)" },
  { key: "gt_iii",   label: "Grau Técnico III (Prismática)" },
  { key: "diamante", label: "Grau Diamante" },
];

export const SUPORTES = [
  { key: "poste_simples", label: "Poste simples" },
  { key: "poste_duplo",   label: "Poste duplo" },
  { key: "bandeira",      label: "Bandeira" },
  { key: "semi_portico",  label: "Semi-pórtico" },
  { key: "portico",       label: "Pórtico" },
];

export const LADOS_VIA = [
  { key: "Direito",          label: "Direito" },
  { key: "Esquerdo",         label: "Esquerdo" },
  { key: "Canteiro Central", label: "Canteiro Central" },
];

// Códigos com desenho real (padrão CONTRAN) disponível em /public/placas/{codigo}.svg
export const PLACAS_DESENHO = ["A-10a", "A-10b", "A-11a", "A-11b", "A-12", "A-13a", "A-13b", "A-14", "A-15", "A-16", "A-17", "A-18", "A-19", "A-1a", "A-1b", "A-20a", "A-20b", "A-21a", "A-21b", "A-21c", "A-21d", "A-21e", "A-22", "A-23", "A-24", "A-25", "A-26a", "A-26b", "A-27", "A-28", "A-29", "A-2a", "A-2b", "A-30a", "A-30b", "A-31", "A-32a", "A-32b", "A-33a", "A-33b", "A-34", "A-35", "A-36", "A-37", "A-38", "A-39", "A-3a", "A-3b", "A-40", "A-42a", "A-42b", "A-42c", "A-43", "A-44", "A-45", "A-46", "A-4a", "A-4b", "A-5a", "A-5b", "A-6", "A-7a", "A-7b", "A-8", "A-9", "R-1", "R-10", "R-11", "R-12", "R-13", "R-14", "R-15", "R-16", "R-17", "R-18", "R-19", "R-2", "R-20", "R-21", "R-22", "R-23", "R-24a", "R-24b", "R-25a", "R-25b", "R-25c", "R-25d", "R-26", "R-27", "R-28", "R-29", "R-3", "R-30", "R-31", "R-32", "R-33", "R-34", "R-35a", "R-35b", "R-36a", "R-36b", "R-37", "R-38", "R-39", "R-40", "R-4a", "R-4b", "R-5a", "R-5b", "R-6a", "R-6b", "R-6c", "R-7", "R-8a", "R-8b", "R-9"];
